// src/admin/sections/HealthSection.jsx
//
// Ticket 35. The screen that makes the quiet failure visible: the text saved
// and the vector didn't.
//
// TWO EMBEDDING STATES, NOT THREE. `books` stores embedding,
// embedding_source_hash and embedded_at and has no status column, so "failed"
// and "never tried" look the same (20260902000000). There is no "failed" tab
// because nothing records one.
//
//   No matching data yet   an author description is saved and the embedding is empty
//   Stale                  a vector exists, and is_recommendable is false: the stored
//                          hash no longer matches the text. Matching is running on
//                          old text. (A stale book still matches, on its old vector:
//                          match_books never reads is_recommendable.)
//   No author description  live, and not author-described. Split by whether there is
//                          anyone to chase.
//
// THE TEXT. The hash covers ONE string: the description, the archived reader
// description, and the Length, Complexity and Romance Level words
// (book_embedding_source(), 20261007000000). So a claimed book is checked once,
// and changing any of the three facts makes a book stale too. This screen asks
// the database (is_recommendable) rather than recomputing the hash, so it cannot
// disagree with it.
//
// "AUTHOR-DESCRIBED" IS DELIBERATELY NOT "DESCRIBED". A reader-written
// description is embedded and does feed matching, so counting every description
// would overstate how much of the shelf can be handed to someone. The count says
// author-described, and reader copy is its own number.
//
// is_author_described is GENERATED from description_source = 'author', so a book
// with no description_source holds NULL there, not false. "Not author-described"
// is therefore `is_author_described IS NOT TRUE`; `.eq(..., false)` would
// silently drop every one of those books.
//
// EVERY NUMBER IS A SERVER COUNT (head: true), not the length of a loaded list. A
// count that fails shows "—", never 0: an unknown is not a zero.
//
// RETRY. Both retries say plainly what happened; neither is ever a silent no-op.
//   Retry one   requestBookReembed (src/lib/reembedBook.js), the single-book mode of
//               batch-reembed-books. Not embed-book: that only runs for the book's
//               own author, so an admin can't use it.
//   Retry all   requestBatchReembed (src/lib/batchReembed.js), the batch mode. Its
//               own numbers are shown; there is no second batch loop here.
//
// THE DESCRIPTION CONTROL. addDescriptionToBook on the Approval Queue (only on a
// held row labelled 'description') writes through set_reader_description(), so it
// is recorded as reader copy and never overwrites text already on the book.
// Ticket 43 removes that label, so the control lives here too rather than being
// lost. Same write, same re-embed after it, same honest message.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { requestBookReembed } from '../../lib/reembedBook';
import { requestBatchReembed } from '../../lib/batchReembed';
import { useTabParam } from '../../hooks/useTabParam';
import { logAudit } from '../lib/auditLog';
import { SETS, NOAUTHOR_SUB, HEALTH_KEYS, loadCounts } from '../lib/healthCounts';
import InfoTip from '../components/InfoTip';
import BackgroundJobs from '../components/BackgroundJobs';

const PAGE = 25;

const ROW_COLUMNS = 'id, title, author_name, status, author_id, description, reader_description, description_source, length, complexity, romance_level, is_recommendable, embedded_at, claimant:authors!books_author_id_fkey ( id, display_name, email )';

const TABS = [
  { id: 'nodata',   label: 'No matching data yet' },
  { id: 'stale',    label: 'Stale' },
  { id: 'noauthor', label: 'No author description' },
];

const fmt = n => (n === null || n === undefined ? '—' : n.toLocaleString());

const hasReaderCopy = b => b.description_source === 'reader' || !!(b.reader_description && b.reader_description.trim());
const hasAnyText = b => !!((b.description && b.description.trim()) || (b.reader_description && b.reader_description.trim()));

// What Retry one answered, in words. The answer comes straight from the function:
// embedded, skipped (unchanged or no_description), limited, or failed with a reason.
export function describeRetry(r) {
  if (r.ok && r.result === 'embedded') return { tone: 'ok', text: 'Embedded. Its matching data is current now.' };
  if (r.ok && r.result === 'skipped' && r.reason === 'unchanged') {
    return { tone: 'ok', text: 'Nothing to do: the stored vector already matches the text. Reload the list if it still shows here.' };
  }
  if (r.ok && r.result === 'skipped' && r.reason === 'no_description') {
    return { tone: 'warn', text: 'Skipped: this book has no description text to embed.' };
  }
  if (r.ok) return { tone: 'warn', text: `Skipped (${r.reason || r.result || 'no reason given'}).` };
  if (r.limited) return { tone: 'warn', text: `Not done: ${r.reason}` };
  return { tone: 'bad', text: `Failed: ${r.reason || 'no reason given'}` };
}

const TONE = {
  ok:   { bg: 'var(--feedback-success-bg)', color: 'var(--feedback-success)', border: 'var(--feedback-success)' },
  warn: { bg: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
  bad:  { bg: 'var(--feedback-error-bg)', color: 'var(--feedback-error)', border: 'var(--feedback-error)' },
};

function Pill({ children, tone = 'neutral' }) {
  const t = TONE[tone] || { bg: '#f5f0e8', color: 'var(--neutral-600)', border: 'var(--brand-sand)' };
  return (
    <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, disabled, primary, isMobile, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: isMobile ? '0 16px' : '7px 14px', minHeight: isMobile ? 44 : undefined,
        background: primary ? 'var(--brand-forest)' : 'var(--brand-white)',
        color: primary ? 'var(--brand-white)' : 'var(--neutral-600)',
        border: primary ? 'none' : '1px solid var(--brand-sand)',
        borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, fontFamily: 'var(--font-body)',
      }}
    >
      {children}
    </button>
  );
}

function Stat({ label, tip, value, sub }) {
  return (
    <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}<InfoTip text={tip} />
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--neutral-800)', lineHeight: 1.1, marginTop: 4 }}>{fmt(value)}</div>
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SourceText({ book }) {
  const facts = [book.length, book.complexity, book.romance_level].filter(Boolean).join(' · ');
  const box = { fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', background: '#faf8f4', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', padding: '8px 10px', whiteSpace: 'pre-wrap', margin: '4px 0 0' };
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase' }}>Description ({book.description_source || 'no source recorded'})</div>
      <p style={box}>{book.description?.trim() || 'none'}</p>
      {book.reader_description?.trim() && (
        <>
          <div style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', marginTop: 8 }}>Reader description (archived)</div>
          <p style={box}>{book.reader_description.trim()}</p>
        </>
      )}
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginTop: 6 }}>
        Also part of the matched text: {facts || 'Length, Complexity and Romance Level are all blank'}.
      </div>
    </div>
  );
}

function BookRow({ book, tab, isMobile, busy, result, onRetry, onNavigate, onSaveCopy }) {
  const [open, setOpen] = useState(false);
  const [copy, setCopy] = useState('');
  const readerCopy = hasReaderCopy(book);
  const text = hasAnyText(book);
  const chase = book.claimant?.display_name || book.claimant?.email || (book.author_id ? `author ${book.author_id}` : null);
  const canRetry = tab !== 'noauthor' || (text && book.is_recommendable === false);
  const done = result?.tone === 'ok' && /Embedded\./.test(result.text);

  return (
    <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 12 : 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: 'var(--neutral-800)', fontSize: 'var(--font-size-md)' }}>{book.title || `Book ${book.id}`}</div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', marginTop: 2 }}>
            {book.author_name || 'No author name'} · #{book.id}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            <Pill>{book.status}</Pill>
            {book.description_source === 'author' && <Pill tone="ok">author copy</Pill>}
            {readerCopy && <Pill>reader copy</Pill>}
            {tab === 'stale' && book.embedded_at && <Pill tone="warn">embedded {new Date(book.embedded_at).toLocaleDateString()}</Pill>}
            {tab === 'noauthor' && text && (book.is_recommendable
              ? <Pill tone="ok">reader copy is embedded</Pill>
              : <Pill tone="warn">reader copy not embedded yet</Pill>)}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', flexShrink: 0 }}>
          {canRetry && !done && (
            <Btn primary isMobile={isMobile} disabled={busy} onClick={() => onRetry(book)}>
              {busy ? 'Retrying…' : 'Retry embedding'}
            </Btn>
          )}
          {text && <Btn isMobile={isMobile} onClick={() => setOpen(o => !o)}>{open ? 'Hide source text' : 'View source text'}</Btn>}
          <Btn isMobile={isMobile} onClick={() => onNavigate?.(`r-books?book=${book.id}`)} title="Opens this book in Books, with its buy links open.">Open book</Btn>
          {tab === 'noauthor' && chase && (
            <Btn isMobile={isMobile} onClick={() => onNavigate?.('a-authors')} title="Opens Authors. There is no link to one author yet, so find them by name.">Open Authors</Btn>
          )}
        </div>
      </div>

      {tab === 'noauthor' && (
        <div style={{ marginTop: 10, fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)' }}>
          {chase
            ? <>Has an author: <strong>{chase}</strong>. Chase them for a description.</>
            : <>Nobody to chase: a reader added this book, so there is no author account and no Backfill row for it. Someone writes the copy, or it comes down until an author claims it.</>}
        </div>
      )}

      {tab === 'noauthor' && !chase && !text && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexDirection: isMobile ? 'column' : 'row' }}>
          <textarea
            value={copy}
            onChange={e => setCopy(e.target.value)}
            placeholder="What's this book about? Saved as reader copy."
            rows={3}
            style={{ flex: 1, padding: '8px 10px', border: '1px solid #bfdbfe', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', outline: 'none', fontFamily: 'var(--font-body)', resize: 'vertical' }}
          />
          <Btn primary isMobile={isMobile} disabled={busy || !copy.trim()} onClick={() => onSaveCopy(book, copy)}>Save as reader copy</Btn>
        </div>
      )}

      {open && <SourceText book={book} />}

      {result && (
        <div role="status" style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, background: TONE[result.tone].bg, color: TONE[result.tone].color, border: `1px solid ${TONE[result.tone].border}` }}>
          {result.text}
        </div>
      )}
    </div>
  );
}

export default function HealthSection({ onNavigate, isMobile }) {
  const [tabParam, setTab] = useTabParam('tab', 'nodata');
  const tab = TABS.some(t => t.id === tabParam) ? tabParam : 'nodata';
  const [sub, setSub] = useState('all');

  const [counts, setCounts] = useState(null);
  const [countsFailed, setCountsFailed] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [more, setMore] = useState(false);
  const [results, setResults] = useState({});
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [confirmAll, setConfirmAll] = useState(false);
  const [runningAll, setRunningAll] = useState(false);
  const [batch, setBatch] = useState(null);

  // A newer load supersedes an older one: switching tabs while a list is still
  // loading must not paint the old tab's rows under the new tab.
  const loadId = useRef(0);

  const refreshCounts = useCallback(async () => {
    const { counts: c, failed } = await loadCounts(HEALTH_KEYS);
    setCounts(c);
    setCountsFailed(failed);
  }, []);

  const loadRows = useCallback(async (from = 0) => {
    const id = ++loadId.current;
    if (from === 0) { setLoading(true); setListError(null); }
    let q = SETS[tab](supabase.from('books').select(ROW_COLUMNS));
    if (tab === 'noauthor') q = NOAUTHOR_SUB[sub](q);
    const { data, error } = await q.order('id', { ascending: false }).range(from, from + PAGE - 1);
    if (id !== loadId.current) return;
    if (error) {
      console.error('[Health] list failed:', error.message);
      setListError(error.message);
      if (from === 0) setRows([]);
      setLoading(false);
      return;
    }
    setRows(prev => (from === 0 ? data : [...prev, ...data]));
    setMore(data.length === PAGE);
    setLoading(false);
  }, [tab, sub]);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);
  useEffect(() => { loadRows(0); }, [loadRows]);

  const setBusy = (id, on) => setBusyIds(prev => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const setResult = (id, r) => setResults(prev => ({ ...prev, [id]: r }));

  async function retryOne(book) {
    setBusy(book.id, true);
    setResult(book.id, null);
    const r = await requestBookReembed(book.id);
    const said = describeRetry(r);
    setResult(book.id, said);
    setBusy(book.id, false);
    // The numbers move when a book embeds. The row stays on screen, with its
    // answer, until the list is next loaded.
    refreshCounts();
  }

  async function saveCopy(book, text) {
    if (!text.trim()) return;
    setBusy(book.id, true);
    setResult(book.id, null);
    // `data: promo` rather than shorthand `{ data, error }`: check-silent-writes
    // only recognises the named form as a checked write.
    const { data: promo, error } = await supabase.rpc('set_reader_description', { p_book_id: book.id, p_text: text.trim() });
    if (error) {
      setResult(book.id, { tone: 'bad', text: `Not saved: ${error.message}` });
      setBusy(book.id, false);
      return;
    }
    if (!promo?.archived && !promo?.promoted) {
      // Nothing was written. Say that, not "saved".
      setResult(book.id, { tone: 'warn', text: `Not saved: ${promo?.reason || 'this book already has copy from a reader'}.` });
      setBusy(book.id, false);
      return;
    }
    await logAudit('update', 'book', `Added reader copy to "${book.title}" from Health`, book.id);
    // The text a book is matched on just changed, so embed it now rather than
    // leaving it "saved but not searchable". The copy HAS saved; a failed embed is
    // reported as its own problem and never as the save having failed.
    const re = await requestBookReembed(book.id);
    setResult(book.id, re.ok
      ? { tone: 'ok', text: 'Saved as reader copy, and embedded. It is not author-described, so the book is still reference-only.' }
      : { tone: 'warn', text: `Saved as reader copy, but it could not be embedded yet (${re.reason}). Use Retry embedding.` });
    setBusy(book.id, false);
    refreshCounts();
    loadRows(0);
  }

  async function retryAll() {
    setConfirmAll(false);
    setRunningAll(true);
    setBatch(null);
    const r = await requestBatchReembed();
    setBatch(r);
    setRunningAll(false);
    if (r.ok) {
      await logAudit('update', 'book', `Health: Retry all. Embedded ${r.embedded}, skipped ${r.skipped}, failed ${r.failed}`);
    } else if (r.timedOut) {
      // The request was sent and the batch may have embedded books, so it is logged
      // as started with the outcome unknown, not left out as "did not run".
      await logAudit('update', 'book', 'Health: Retry all started, outcome unknown (the screen stopped waiting)');
    }
    refreshCounts();
    loadRows(0);
  }

  const c = counts || {};
  const needsRetry = c.nodata === null || c.stale === null || c.nodata === undefined || c.stale === undefined
    ? null
    : c.nodata + c.stale;
  const pad = isMobile ? 16 : '30px 36px';

  return (
    <div style={{ padding: pad, fontFamily: 'var(--font-body)' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 0 }}>Health</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 4 }}>
          Whether the scheduled jobs are running, whether journal entries are getting embedded, and books whose text saved but whose matching data is missing or old.
        </p>
      </div>

      {countsFailed > 0 && (
        <div role="alert" style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: TONE.warn.bg, color: TONE.warn.color, border: `1px solid ${TONE.warn.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          Couldn&apos;t load {countsFailed === 1 ? 'one count' : `${countsFailed} counts`}. A dash means unknown, not zero.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        <Stat label="Live books" value={c.live} tip="Books with status live. Pending and removed books are left out." />
        <Stat
          label="Author-described"
          value={c.authorDescribed}
          sub={c.live != null && c.authorDescribed != null ? `of ${fmt(c.live)} live books` : undefined}
          tip="Live books whose description an author wrote. This is the number that can be handed to a reader. A description a reader wrote is not counted here."
        />
        <Stat
          label="Reader copy"
          value={c.readerCopy}
          sub={c.noauthor != null && c.readerCopy != null ? `of ${fmt(c.noauthor)} live books with no author description` : undefined}
          tip="Live books with no author description that do have a reader's words. Reader copy is embedded and feeds matching, but it does not make a book recommendable, so it is counted on its own."
        />
        <Stat
          label="Need matching data"
          value={needsRetry}
          sub={needsRetry === null ? undefined : `${fmt(c.nodata)} none yet · ${fmt(c.stale)} stale`}
          tip="Books that need an embedding run: author-described with no vector yet, plus every book with a vector whose text has changed since. Reader-only copy that was never embedded is on the No author description tab."
        />
      </div>

      <BackgroundJobs isMobile={isMobile} />

      <div style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '20px 22px' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
            Retry all runs the whole backlog through the batch tool: every book with text whose matching data is missing or old, reader copy included. It makes one embedding call per book.
          </p>
          {!confirmAll ? (
            <Btn primary isMobile={isMobile} disabled={runningAll} onClick={() => setConfirmAll(true)}>{runningAll ? 'Retrying all…' : 'Retry all'}</Btn>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Btn primary isMobile={isMobile} onClick={retryAll}>Run it now</Btn>
              <Btn isMobile={isMobile} onClick={() => setConfirmAll(false)}>Cancel</Btn>
            </div>
          )}
        </div>

        {batch && !batch.ok && batch.timedOut && (
          <div role="status" style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: TONE.warn.bg, color: TONE.warn.color, border: `1px solid ${TONE.warn.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
            {batch.reason} The counts on this screen were reloaded; check them again in a minute.
          </div>
        )}
        {batch && !batch.ok && !batch.timedOut && (
          <div role="status" style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: TONE.bad.bg, color: TONE.bad.color, border: `1px solid ${TONE.bad.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
            Retry all did not run: {batch.reason}
          </div>
        )}
        {batch && batch.ok && (
          <div role="status" style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', background: (batch.failed > 0 || !batch.done ? TONE.warn : TONE.ok).bg, color: (batch.failed > 0 || !batch.done ? TONE.warn : TONE.ok).color, border: `1px solid ${(batch.failed > 0 || !batch.done ? TONE.warn : TONE.ok).border}` }}>
            <div style={{ fontWeight: 700 }}>
              Embedded {batch.embedded}, skipped {batch.skipped}, failed {batch.failed}.
              {' '}
              {batch.done
                ? 'Nothing is left to retry.'
                : batch.fixable === null
                  ? 'How many are still waiting is unknown.'
                  : `${batch.fixable} still waiting.`}
            </div>
            {batch.stoppedBecause && <div style={{ marginTop: 4 }}>{batch.stoppedBecause}</div>}
            {batch.failures.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {batch.failures.slice(0, 5).map(f => <li key={f.id}>Book #{f.id}: {f.reason}</li>)}
                {batch.failures.length > 5 && <li>…and {batch.failures.length - 5} more.</li>}
              </ul>
            )}
          </div>
        )}

        {/* Scrolls sideways on a phone only. On a wide screen overflow here showed a stray
            scrollbar: the tab buttons sit 2px over the line below them (marginBottom -2),
            which counts as vertical overflow. */}
        <div style={{ display: 'flex', borderBottom: '2px solid var(--brand-sand)', marginBottom: 16, ...(isMobile ? { overflowX: 'auto' } : {}) }}>
          {TABS.map(t => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} style={{
              fontSize: 'var(--font-size-sm)', fontWeight: 600, padding: isMobile ? '0 14px' : '8px 16px', minHeight: isMobile ? 44 : undefined, whiteSpace: 'nowrap',
              color: tab === t.id ? 'var(--neutral-800)' : 'var(--neutral-400)', border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid var(--neutral-800)' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}>
              {t.label} <span style={{ fontWeight: 700 }}>({fmt(c[t.id])})</span>
            </button>
          ))}
        </div>

        {tab === 'nodata' && (
          <p style={{ margin: '0 0 14px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>
            An author description is saved and there is no vector. Until one is built the book can&apos;t be matched. There is no &ldquo;failed&rdquo; tab: nothing records a failed attempt, so a failed embed and one never tried look the same.
          </p>
        )}
        {tab === 'stale' && (
          <p style={{ margin: '0 0 14px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>
            The text changed after its vector was built, so matching is running on the old text. A stale book still matches, on its old vector. The text is the description, any archived reader description, and the Length, Complexity and Romance Level words, checked once.
          </p>
        )}
        {tab === 'noauthor' && (
          <>
            <p style={{ margin: '0 0 10px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>
              Live, and reference-only: nobody can be matched to these. Where a reader wrote the copy the row says so.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              {[
                ['all', 'All', c.noauthor],
                ['author', 'Has an author to chase', c.noauthorAuthor],
                ['nobody', 'Nobody to chase', c.noauthorNobody],
              ].map(([id, label, n]) => (
                <button key={id} type="button" onClick={() => setSub(id)} style={{
                  padding: isMobile ? '0 14px' : '5px 12px', minHeight: isMobile ? 44 : undefined, borderRadius: 'var(--radius-pill)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)',
                  background: sub === id ? 'var(--neutral-800)' : 'var(--brand-white)', color: sub === id ? 'var(--brand-white)' : 'var(--neutral-600)', border: '1px solid var(--brand-sand)',
                }}>{label} ({fmt(n)})</button>
              ))}
            </div>
          </>
        )}

        {loading ? (
          <div style={{ padding: 'var(--space-xxl)', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading…</div>
        ) : listError ? (
          <div role="alert" style={{ padding: '12px 14px', borderRadius: 'var(--radius-md)', background: TONE.bad.bg, color: TONE.bad.color, border: `1px solid ${TONE.bad.border}`, fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
            Couldn&apos;t load this list: {listError}
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '36px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
            ✓ Nothing here
          </div>
        ) : (
          <>
            {rows.map(b => (
              <BookRow
                key={b.id}
                book={b}
                tab={tab}
                isMobile={isMobile}
                busy={busyIds.has(b.id)}
                result={results[b.id]}
                onRetry={retryOne}
                onNavigate={onNavigate}
                onSaveCopy={saveCopy}
              />
            ))}
            {more && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Btn isMobile={isMobile} onClick={() => loadRows(rows.length)}>Show more</Btn>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
